import { AGENT_REMOTE_EDIT_REVIEW_DATA } from '@atlaskit/editor-common/collab-agent-remote-edit-review';
import type { AgentRemoteEditReviewData } from '@atlaskit/editor-common/collab-agent-remote-edit-review';
import { SafePlugin } from '@atlaskit/editor-common/safe-plugin';
import type {
	EditorCommand,
	ExtractInjectionAPI,
	PMPluginFactoryParams,
} from '@atlaskit/editor-common/types';
import type { GetMentionNameDetails } from '@atlaskit/editor-mention-types/types';
import type {
	EditorState,
	ReadonlyTransaction,
	Transaction,
} from '@atlaskit/editor-prosemirror/state';
import type { EditorView } from '@atlaskit/editor-prosemirror/view';
import { expValEqualsNoExposure } from '@atlaskit/tmp-editor-statsig/exp-val-equals-no-exposure';

import type { AIPlugin } from '../../aiPluginType';
import { openPostStreamReviewModal } from '../../editor-actions/actions';
import type { ErrorAIEvent, StreamAIEvent } from '../../editor-actions/types';
import type { AIGlobalOptIn, EditorPluginAIProvider } from '../../types/types';
import { configItemUsesRovo } from '../../ui/components/SessionUI/utils/checkMigrationStatus';
import { isNewAifcExperienceEnabled } from '../../ui/utils/new-aifc-experience-enabled';
import { countCompleteBlocks, takeCompleteBlocks } from '../../ui/utils/complete-blocks';
import { isQuickActionsBlockStreamingEnabled } from '../../ui/utils/quick-actions-block-streaming-enabled';
import { createStreamFlowPacer } from '../../ui/utils/request/stream-flow-pacer';
import { createStreaming } from '../../ui/utils/streaming';
import { aiExperienceDecorationPluginKey } from '../decoration/decoration-plugin-key';
import { purePmInEditorDirectStreamingPluginKey } from '../pure-pm-in-editor-direct-streaming/pluginKey';

import { aiSessionContextPluginKey } from './ai-session-context-plugin-key';
import {
	streamADFResponse,
	completeADFStream,
	errorADFStream,
	resetSessionState,
	updateIdMap,
	updateTraceIds,
} from './commands';
import {
	AI_CONTENT_SEGMENT_DATA,
	type AIContentSegmentData,
	AI_FINAL_STREAMING_TRANSFORMATION_DATA,
	type AIFinalStreamingTransformationData,
	AI_ORIGINAL_NODE_DATA,
	type AIOriginalNodeData,
	CAPTURE_ABORTED_TOOL_CALL_POSITIONS,
	type CaptureAbortedToolCallPositionsData,
} from './meta-keys';
import { reducer } from './reducer';
import type {
	AIContentPositionEntry,
	SessionContextPluginState,
	SessionContextAction,
} from './types';
import { createUndoneSegmentDecorationProvider } from './undone-segment-decorations';

// [CCI-17994] How long to wait after the last BE-streaming agent-edit batch before
// opening the Review moment (unless the batch was flagged `complete`, which opens
// immediately). A plain constant — the spike is not gated behind an experiment.
const AGENT_REVIEW_DEBOUNCE_MS = 100;

export const initialState: SessionContextPluginState = {
	adfResponse: undefined,
	status: 'idle',
	responseVersion: 0,
	streamPaused: false,
	hasUserScrolled: false,
	// [CCI-15904] New AIFC Editor Experience — tracks AI-produced top-level change
	// ranges across a single session for the Post Stream Review toolbar.
	aiContentPositions: [],
	// Positions of tool calls aborted by the queue timeout, keyed by id. Remapped
	// on every doc change and consumed on resume.
	abortedToolCallPositions: {},
	// Id of a tool call awaiting final-position capture in the next
	// buildCommitTransaction; cleared once captured.
	pendingForceAbortedToolCallId: undefined,
};

export interface AISessionContextPluginOptions {
	/**
	 * [CCI-15904] The global AI opt-in state. Required by `openPostStreamReviewModal`
	 * → `openAIModal` to gate the Post Stream Review modal behind FedRAMP / opt-in
	 * checks when auto-opening it after a quick-action stream completes.
	 */
	aiGlobalOptIn: AIGlobalOptIn;
	api: ExtractInjectionAPI<AIPlugin> | undefined;
	dispatch: PMPluginFactoryParams['dispatch'];
	editorPluginAIProvider: EditorPluginAIProvider;
	getIntl: PMPluginFactoryParams['getIntl'];
	getMentionNameDetails: GetMentionNameDetails;
}

/**
 * This plugin stores the core business logic state for the AI session.
 * It manages the session lifecycle including:
 * - State transitions (idle → streaming → complete)
 * - Streaming initiation for non-orchestrator prompts
 * - Bridging stream events back to plugin state
 *
 * The view.update() method detects when streaming should start (based on decoration state)
 * and initiates the network request, bridging responses back to this plugin's state.
 * The pure-pm plugin then reacts to state changes to update the document.
 */
export const createAISessionContextPlugin = (
	options: AISessionContextPluginOptions,
): SafePlugin => {
	const { aiGlobalOptIn, api, dispatch, editorPluginAIProvider, getIntl, getMentionNameDetails } =
		options;

	// Per-plugin-instance memoised provider for the undone-segment
	// gray-background decoration. Created once here so its cache never leaks
	// across editor instances.
	const getUndoneSegmentDecorations = createUndoneSegmentDecorationProvider();

	return new SafePlugin<SessionContextPluginState>({
		key: aiSessionContextPluginKey,
		state: {
			init: () => initialState,
			apply: (tr: ReadonlyTransaction, pluginState: SessionContextPluginState) => {
				let nextState: SessionContextPluginState = pluginState;

				// [CCI-15904] Remap stored AI content positions through tr.mapping on every
				// doc-changing transaction so positions remain accurate across collab edits,
				// undo/redo, and subsequent AI tool-call commits within the same session.
				if (tr.docChanged && nextState.aiContentPositions?.length) {
					const remapped = nextState.aiContentPositions.map((entry: AIContentPositionEntry) => {
						const mappedStart = tr.mapping.map(entry.startPos, 1);
						const mappedEnd = tr.mapping.map(entry.endPos, -1);
						// The start/end biases (start after preceding inserts, end before
						// following inserts) can cross when content is re-inserted exactly
						// at the stored range — e.g. undoing a `remove` segment restores the
						// removed content at that point, pushing `startPos` past `endPos` and
						// leaving them swapped. Normalise so the lower value is always the
						// start and the higher value the end, otherwise a subsequent redo
						// resolves an inverted range and applies at the wrong position.
						return {
							...entry,
							startPos: Math.min(mappedStart, mappedEnd),
							endPos: Math.max(mappedStart, mappedEnd),
						};
					});
					nextState = { ...nextState, aiContentPositions: remapped };
				}

				// Remap the fine step-through segments with the SAME bias
				// handling as `aiContentPositions`, so a per-segment undo/redo (which
				// changes the doc) keeps every OTHER fine segment's range accurate. This
				// is what makes per-segment undo/redo correct when one coarse commit was
				// split into multiple fine segments.
				// [CCI-17658] Also remap nested `diffSegments` so diff-level positions
				// stay accurate after doc changes.
				if (tr.docChanged && nextState.streamReviewSegments?.length) {
					const remapEntry = (entry: AIContentPositionEntry): AIContentPositionEntry => {
						const mappedStart = tr.mapping.map(entry.startPos, 1);
						const mappedEnd = tr.mapping.map(entry.endPos, -1);
						return {
							...entry,
							startPos: Math.min(mappedStart, mappedEnd),
							endPos: Math.max(mappedStart, mappedEnd),
							diffSegments: entry.diffSegments?.map(remapEntry),
						};
					};
					nextState = {
						...nextState,
						streamReviewSegments: nextState.streamReviewSegments.map(remapEntry),
					};
				}

				// Remap the rework-range highlight on doc change with the same
				// start-after/end-before bias as the other stored ranges.
				if (tr.docChanged && nextState.reworkHighlightRange) {
					const mappedStart = tr.mapping.map(nextState.reworkHighlightRange.startPos, 1);
					const mappedEnd = tr.mapping.map(nextState.reworkHighlightRange.endPos, -1);
					nextState = {
						...nextState,
						reworkHighlightRange: {
							startPos: Math.min(mappedStart, mappedEnd),
							endPos: Math.max(mappedStart, mappedEnd),
						},
					};
				}

				// Remap stored aborted-tool-call positions on every doc change so a
				// resumed tool call re-applies where it was originally working,
				// accounting for content other tool calls inserted/removed after it was
				// aborted. Same bias as aiContentPositions: start after preceding
				// inserts (1), end before following inserts (-1).
				if (tr.docChanged && nextState.abortedToolCallPositions) {
					const ids = Object.keys(nextState.abortedToolCallPositions);
					if (ids.length) {
						const remappedAborted: NonNullable<
							SessionContextPluginState['abortedToolCallPositions']
						> = {};
						for (const id of ids) {
							const entry = nextState.abortedToolCallPositions[id];
							remappedAborted[id] = {
								startPos: tr.mapping.map(entry.startPos, 1),
								aiContentEndPos: tr.mapping.map(entry.aiContentEndPos, -1),
							};
						}
						nextState = { ...nextState, abortedToolCallPositions: remappedAborted };
					}
				}

				// [CCI-15904] Append a new AI content position entry when the transaction
				// carries the AI_FINAL_STREAMING_TRANSFORMATION_DATA meta.
				// Set by `buildCommitTransaction` when the New AIFC Editor Experience gate is ON.
				//
				// We intentionally do NOT gate this on `nextState.sessionId`: for quick actions
				// (Improve writing, Change tone, etc.) `startSession` is never called, so
				// `sessionId` is `undefined` even though `buildCommitTransaction` has fired and
				// `aiContentPositions` must still be populated for the Post Stream Review modal.
				const finalStreamData = tr.getMeta(AI_FINAL_STREAMING_TRANSFORMATION_DATA) as
					| AIFinalStreamingTransformationData
					| undefined;
				if (finalStreamData) {
					const commitStart = finalStreamData.positions.mappedStartPos;
					const commitEnd = finalStreamData.positions.mappedToPos;
					const coarseEntry = {
						startPos: commitStart,
						endPos: commitEnd,
						slices: [finalStreamData.originalUserContentSlice, finalStreamData.newSlice],
						appliedIndex: 1 as const,
						kind: finalStreamData.kind,
					};

					// [CCI-17658] Per-segment rework mutations are now carried on
					// final-stream meta and reduced through explicit REWORK_* actions.
					if (finalStreamData.reworkMutation && nextState.streamReviewSegments?.length) {
						nextState = reducer({
							state: nextState,
							action: {
								type: 'REWORK_APPEND_SLICE',
								topLevelIndex: finalStreamData.reworkMutation.topLevelIndex,
								startPos: commitStart,
								endPos: commitEnd,
								newSlice: finalStreamData.newSlice,
								originalUserContentSlice: finalStreamData.originalUserContentSlice,
							},
						});
						nextState = { ...nextState, replaceNodeOriginalNode: undefined };
					} else {
						// Fresh session: create a new entry as before.
						nextState = {
							...nextState,
							aiContentPositions: [...(nextState.aiContentPositions ?? []), coarseEntry],
							replaceNodeOriginalNode: undefined,
						};
					}
				}

				// Append coarse AI content position entries recorded by a
				// NON-STREAMABLE command handler (removeNode, setAttrs, move, wrap, ...).
				// These commands mutate the doc directly and never reach
				// `buildCommitTransaction`, so they stamp AI_CONTENT_SEGMENT_DATA instead.
				// Consumed identically to the streamable path so the Post Stream Review
				// modal can navigate / undo / redo them.
				const segmentData = tr.getMeta(AI_CONTENT_SEGMENT_DATA) as AIContentSegmentData | undefined;
				if (segmentData && segmentData.segments.length > 0) {
					nextState = {
						...nextState,
						aiContentPositions: [
							...(nextState.aiContentPositions ?? []),
							// [CCI-17658] Seed each non-streamable segment with [original, AI].
							...segmentData.segments.map((segment) => ({
								startPos: segment.startPos,
								endPos: segment.endPos,
								slices: [segment.originalUserContentSlice, segment.newSlice],
								appliedIndex: 1,
								kind: segment.kind,
							})),
						],
					};
				}

				// [CCI-17994] BE-streaming Review moment: collab-edit's `applyRemoteSteps`
				// recorded a batch of remote agent steps via the neutral
				// `AGENT_REMOTE_EDIT_REVIEW_DATA` meta (editor-common). Append coarse
				// `aiContentPositions` entries exactly like the FE streaming path, so the
				// whole Review moment pipeline is reused. Bump the nonce so `view()` can
				// (re)arm the open debounce (or the already-open PSR screen can append the
				// change).
				//
				// Requester-scoped: agent steps reach every collaborator, so only the
				// requester's client records (and thus opens) the Review moment.
				// `isLocalUserRequester` is fail-closed (`undefined` ⇒ not the requester).
				const agentRemoteEditData = tr.getMeta(AGENT_REMOTE_EDIT_REVIEW_DATA) as
					| AgentRemoteEditReviewData
					| undefined;
				if (
					agentRemoteEditData &&
					agentRemoteEditData.isLocalUserRequester &&
					agentRemoteEditData.segments.length > 0
				) {
					nextState = {
						...nextState,
						aiContentPositions: [
							...(nextState.aiContentPositions ?? []),
							...agentRemoteEditData.segments.map((segment) => ({
								startPos: segment.startPos,
								endPos: segment.endPos,
								slices: [segment.originalSlice, segment.newSlice],
								appliedIndex: 1,
								kind: segment.kind,
							})),
						],
						agentRemoteEditNonce: (nextState.agentRemoteEditNonce ?? 0) + 1,
						agentRemoteEditComplete: !!agentRemoteEditData.complete,
					};
				}

				// Store the node a `replaceNode` command recorded so
				// `buildMountTransaction` can rebuild an accurate single-node
				// `originalUserContentSlice`. Transient field only — not a reviewable
				// segment, so `aiContentPositions` is untouched.
				const originalNodeData = tr.getMeta(AI_ORIGINAL_NODE_DATA) as
					| AIOriginalNodeData
					| undefined;
				if (originalNodeData) {
					nextState = {
						...nextState,
						replaceNodeOriginalNode: originalNodeData.originalNode,
					};
				}

				// Persist a force-finished tool call's final positions from the meta
				// stamped by `buildCommitTransaction`. Stored by id (then remapped on
				// every later doc change above); captured exactly once.
				const capturedAbortedPositions = tr.getMeta(CAPTURE_ABORTED_TOOL_CALL_POSITIONS) as
					| CaptureAbortedToolCallPositionsData
					| undefined;
				if (capturedAbortedPositions) {
					nextState = {
						...nextState,
						abortedToolCallPositions: {
							...nextState.abortedToolCallPositions,
							[capturedAbortedPositions.toolCallId]: {
								startPos: capturedAbortedPositions.startPos,
								aiContentEndPos: capturedAbortedPositions.aiContentEndPos,
							},
						},
						pendingForceAbortedToolCallId: undefined,
					};
				}

				// Existing behaviour — process dispatched plugin-key actions.
				const meta = tr.getMeta(aiSessionContextPluginKey);
				if (meta) {
					nextState = reducer({
						state: nextState,
						action: meta as SessionContextAction,
					});
				}

				if (nextState !== pluginState) {
					dispatch(aiSessionContextPluginKey, nextState);
				}
				return nextState;
			},
		},
		props: {
			// Shade every undone Post Stream Review segment
			// (`appliedIndex === 0`) with a gray background, so undoing a segment
			// has a visible cue in the document. Derived from this plugin's own
			// state (which `apply()` remaps on every doc change) via a memoised
			// provider, so navigation transactions that don't change the doc reuse
			// the cached set instead of reallocating one on every view update.
			decorations(editorState: EditorState) {
				const pluginState = aiSessionContextPluginKey.getState(editorState);
				if (!pluginState) {
					return null;
				}
				return getUndoneSegmentDecorations(editorState.doc, pluginState);
			},
		},
		/**
		 * When all prompts are migrated to use the orchestrator, this entire view can be removed.
		 */
		view: () => {
			// Replaced on stop: a stopped stream's request keeps running and pushing into
			// its pacer, which would otherwise emit into the next stream's session.
			// One-chunk apply has nothing to pace, so the response is delivered as soon
			// as the request drains.
			let streamFlowPacer = isQuickActionsBlockStreamingEnabled() ? null : createStreamFlowPacer();
			// Owned here so a stop can abort mid-flight — `createStreaming` only returns
			// its abort cleanup once the response has fully drained.
			let abortController: AbortController | undefined = expValEqualsNoExposure(
				'platform_editor_remove_pause_streaming',
				'isEnabled',
				true,
			)
				? new AbortController()
				: undefined;
			let abortStreaming: (() => void) | undefined;
			let hasStartedStreaming = false;
			// Streaming setup is async; if cancel/reset happens before cleanup is available,
			// this "sticky" flag ensures we abort immediately once cleanup resolves.
			// It also guards against late/buffered events reactivating plugin state after cancel/reset.
			let cancelled = false;

			// [CCI-17994] BE-streaming Review moment open debounce. Armed in update()
			// when a remote agent-edit batch is recorded (nonce change); fires
			// `openAgentReview` once the agent settles.
			let agentReviewTimer: ReturnType<typeof setTimeout> | undefined;
			const clearAgentReviewTimer = () => {
				if (agentReviewTimer !== undefined) {
					clearTimeout(agentReviewTimer);
					agentReviewTimer = undefined;
				}
			};
			const openAgentReview = (targetView: EditorView) => {
				if (targetView.isDestroyed) {
					return;
				}
				// The Review moment reuses the FE streaming screen, which only renders
				// under the New AIFC Editor Experience — so BE review requires those gates
				// on too. Bail (rather than open a broken legacy screen) otherwise.
				if (!isNewAifcExperienceEnabled()) {
					return;
				}
				const latest = aiSessionContextPluginKey.getState(targetView.state);
				const positions = latest?.aiContentPositions ?? [];
				const baseGenerate = editorPluginAIProvider.baseGenerate;
				if (!positions.length || !baseGenerate) {
					return;
				}
				// Opening the modal sets a `configItem` on the decoration plugin, which the
				// streaming trigger below would otherwise mistake for "start a new stream".
				// Mark streaming as started so that path stays inert for this review-only
				// open (reset by `cancelStreaming` when the review closes and status returns
				// to idle).
				hasStartedStreaming = true;
				// Aggregate over every recorded change, anchored below the last one —
				// mirrors the FE quick-action auto-open.
				const aggregateStart = Math.min(...positions.map((entry) => entry.startPos));
				const aggregateEnd = Math.max(...positions.map((entry) => entry.endPos));
				openPostStreamReviewModal({
					editorView: targetView,
					baseGenerate,
					aiOptIn: aiGlobalOptIn,
					mode: 'aggregate',
					segmentData: {
						segmentIndex: positions.length - 1,
						positions: { startPos: aggregateStart, endPos: aggregateEnd },
					},
				});
			};

			const cancelStreaming = () => {
				cancelled = true;
				hasStartedStreaming = false;
				if (expValEqualsNoExposure('platform_editor_remove_pause_streaming', 'isEnabled', true)) {
					streamFlowPacer?.destroy();
					streamFlowPacer = isQuickActionsBlockStreamingEnabled() ? null : createStreamFlowPacer();
					abortController?.abort();
					abortController = new AbortController();
				}
				if (abortStreaming) {
					abortStreaming();
					abortStreaming = undefined;
				}
			};

			return {
				update(view: EditorView, previousState: EditorState) {
					const sessionState = aiSessionContextPluginKey.getState(view.state);
					const prevSessionState = aiSessionContextPluginKey.getState(previousState);
					const decorationState = aiExperienceDecorationPluginKey.getState(view.state);
					const prevDecorationState = aiExperienceDecorationPluginKey.getState(previousState);

					// [CCI-17994] BE-streaming Review moment. A remote agent-edit batch was
					// recorded on this (the requester's) client — the nonce changed. Debounce
					// so the Review moment opens once the agent settles (no new batch for
					// `reviewDebounceMs`); a batch flagged terminal by the backend (hybrid)
					// opens immediately. Placed before the idle-return below because BE
					// streaming leaves `status` idle (no FE session was ever started).
					const agentNonce = sessionState?.agentRemoteEditNonce;
					if (agentNonce !== undefined && agentNonce !== prevSessionState?.agentRemoteEditNonce) {
						clearAgentReviewTimer();
						// When the Review moment is already open, a new agent batch is
						// appended to the LIVE review by the PSR screen (it recomputes
						// streamReviewSegments off the grown aiContentPositions). Nothing to
						// open/debounce here — only drive the open when review is not yet showing.
						if (sessionState?.status !== 'postStreamReview') {
							if (sessionState?.agentRemoteEditComplete) {
								openAgentReview(view);
							} else {
								agentReviewTimer = setTimeout(() => {
									agentReviewTimer = undefined;
									openAgentReview(view);
								}, AGENT_REVIEW_DEBOUNCE_MS);
							}
						}
					}

					// Handle cancel request - detect when cancelRequested flag was just set
					const cancelJustRequested =
						sessionState?.cancelRequested && !prevSessionState?.cancelRequested;
					if (cancelJustRequested) {
						cancelStreaming();
					}

					// Reset streaming flag and abort any in-flight request when session ends
					// This handles both explicit cancel and implicit reset
					if (sessionState?.status === 'idle') {
						cancelStreaming();
						return;
					}

					// For the fallback free-gen path: reset session state after finalize completes.
					// The 'complete' event handler defers the reset so buildCommitTransaction can
					// still read markdown/idMap from session state during the finalize flow.
					// Once finalize is done (hasFinalStream: true), it's safe to reset.
					if (sessionState?.status === 'complete' && sessionState?.userInput !== undefined) {
						const streamingState = purePmInEditorDirectStreamingPluginKey.getState(view.state);
						if (streamingState?.hasFinalStream) {
							api?.core?.actions.execute(resetSessionState());
							return;
						}
					}

					// When streamPaused changes, pause or resume the pacer so the discard screen can stop delivery
					if (sessionState?.streamPaused !== prevSessionState?.streamPaused) {
						if (sessionState?.streamPaused) {
							streamFlowPacer?.pause();
						} else {
							streamFlowPacer?.resume();
						}
					}

					// Detect when we should start streaming:
					// - configItem just became available (decoration state changed)
					// - Not a Rovo/orchestrator prompt
					// - Haven't already started streaming for this session
					const configItemJustSet = decorationState?.configItem && !prevDecorationState?.configItem;
					const shouldStartStream =
						configItemJustSet &&
						decorationState?.positions &&
						!hasStartedStreaming &&
						!configItemUsesRovo(decorationState.configItem?.key);

					// Detect when the fallback free-gen prompt submits user input.
					// This happens when START_FREE_GEN_STREAM action is dispatched from the PromptScreen
					// for products that don't have Rovo/onSubmit (e.g. Jira, Bitbucket).
					const freeGenStreamJustStarted =
						sessionState?.status === 'streaming' &&
						prevSessionState?.status === 'prompt' &&
						sessionState?.userInput !== undefined &&
						!hasStartedStreaming &&
						decorationState?.configItem &&
						decorationState?.positions;

					if (!shouldStartStream && !freeGenStreamJustStarted) {
						return;
					}

					if (!decorationState?.configItem || !decorationState?.positions) {
						return;
					}

					hasStartedStreaming = true;
					cancelled = false;

					// Create the streaming handler
					// Note: Analytics for streaming is handled separately via the AnalyticsFlow context
					// in the React layer. This needs fixing
					const streamingHandler = createStreaming({
						abortController,
						fireAIAnalyticsEvent: () => {},
						streamFlowPacer,
					});

					// Build the request context
					const positions = decorationState.positions;
					const isEmpty = positions[0] === positions[1];
					const selectionType = isEmpty ? 'empty' : 'range';
					const context = {
						aiProvider: editorPluginAIProvider,
						intl: getIntl(),
						editorView: view,
						positions,
						isEmpty,
						selectionType: selectionType as 'empty' | 'range',
						configItem: decorationState.configItem,
						userInput: freeGenStreamJustStarted ? (sessionState?.userInput ?? '') : '',
						analyticsContext: { aiSessionId: '', aiGroupId: '' },
						usePageKnowledge: true,
						getMentionNameDetails,
						responseHistory: { entries: [], positionFromEnd: 0, totalSize: 0, traversals: 0 },
					};

					// Block streaming publishes whole blocks only, so the document snaps forward
					// a block at a time instead of character by character. The latest chunk is
					// retained as a fallback for terminal events that arrive without content.
					const blockStreaming = isQuickActionsBlockStreamingEnabled();
					let resolved: Pick<StreamAIEvent, 'adfResponse' | 'markdown'> = {};
					let publishedBlocks = 0;

					// Invoke streaming and bridge events to session context
					streamingHandler(context, (event) => {
						if (cancelled) {
							return;
						}
						switch (event.type) {
							case 'stream':
								if (event.adfResponse || event.markdown) {
									if (blockStreaming) {
										resolved = {
											adfResponse: event.adfResponse,
											markdown: event.markdown,
										};

										// Publish only whole blocks, so the document snaps forward a
										// block at a time rather than character by character. The
										// trailing block is withheld until the stream terminates.
										const completeBlocks = countCompleteBlocks(resolved);
										if (completeBlocks > publishedBlocks) {
											publishedBlocks = completeBlocks;
											api?.core?.actions.execute(
												streamADFResponse({
													type: 'stream',
													...takeCompleteBlocks(resolved, completeBlocks),
												}),
											);
										}
										break;
									}
									api?.core?.actions.execute(
										streamADFResponse({
											type: 'stream',
											adfResponse: event.adfResponse,
											markdown: event.markdown,
										}),
									);
								}
								break;
							case 'complete':
								resolved = blockStreaming
									? {
											adfResponse: event.adfResponse ?? resolved.adfResponse,
											markdown: event.markdown ?? resolved.markdown,
										}
									: { adfResponse: event.adfResponse, markdown: event.markdown };

								// The finaliser only commits after the session has passed through
								// `streaming`. A single-block response never reaches a block
								// boundary mid-stream, so the response is published as one stream
								// immediately before completing.
								if (
									blockStreaming &&
									publishedBlocks === 0 &&
									(resolved.adfResponse || resolved.markdown)
								) {
									api?.core?.actions.execute(
										streamADFResponse({
											type: 'stream',
											adfResponse: resolved.adfResponse,
											markdown: resolved.markdown,
										}),
									);
								}

								if (resolved.adfResponse || resolved.markdown) {
									api?.core?.actions.execute(
										completeADFStream({
											type: 'complete',
											adfResponse: resolved.adfResponse,
											markdown: resolved.markdown,
										}),
									);
								}
								// Free-gen path keeps session state alive for the UI layer to reset
								// after accept/reject.
								if (!freeGenStreamJustStarted) {
									// [CCI-15904] Quick-action path: END_SESSION (retains
									// aiContentPositions) + auto-open Post Stream Review modal.
									// Only valid when this 'complete' event carried content (otherwise
									// stale positions from a prior session could be reused) AND
									// baseGenerate is present (needed by SHOW_MODAL). Otherwise fall
									// back to RESET so positions don't leak into the next session.
									const baseGenerate = editorPluginAIProvider.baseGenerate;
									const hasContent = !!(resolved.adfResponse || resolved.markdown);
									// Auto-open the Post Stream Review modal when Review moment is
									// eligible; otherwise FE edits apply and the session resets.
									if (isNewAifcExperienceEnabled() && baseGenerate && hasContent) {
										// Two microtasks: (1) let END_SESSION commit; (2) let the
										// reducer apply it before reading aiContentPositions.
										void (async () => {
											await Promise.resolve();
											if (view.isDestroyed) {
												return;
											}
											api?.core?.actions.execute(createCommand({ type: 'END_SESSION' }));

											await Promise.resolve();
											if (view.isDestroyed) {
												return;
											}
											const latestSessionState = aiSessionContextPluginKey.getState(view.state);
											const reviewSegments = latestSessionState?.streamReviewSegments ?? [];
											if (reviewSegments.length) {
												const displaySegments = reviewSegments.flatMap((segment) =>
													segment.diffSegments?.length ? segment.diffSegments : [segment],
												);
												const desiredIndex =
													latestSessionState?.currentStreamReviewSegmentIndex ?? 0;
												const segmentIndex =
													displaySegments.length > 0
														? Math.max(0, Math.min(desiredIndex, displaySegments.length - 1))
														: 0;
												const segment = displaySegments[segmentIndex];
												if (segment) {
													openPostStreamReviewModal({
														editorView: view,
														baseGenerate,
														aiOptIn: aiGlobalOptIn,
														segmentData: {
															segmentIndex,
															positions: {
																startPos: segment.startPos,
																endPos: segment.endPos,
															},
														},
													});
													return;
												}
											}
											if (latestSessionState?.aiContentPositions?.length) {
												openPostStreamReviewModal({
													editorView: view,
													baseGenerate,
													aiOptIn: aiGlobalOptIn,
													mode: 'aggregate',
												});
											}
										})();
									} else {
										api?.core?.actions.execute(resetSessionState());
									}
								}
								break;
							case 'response too similar':
								api?.core?.actions.execute(
									completeADFStream({
										type: 'complete',
										responseTooSimilar: true,
									}),
								);

								break;
							case 'error':
								if (event.errorInfo) {
									api?.core?.actions.execute(errorADFStream(event as ErrorAIEvent));
								}
								break;
							case 'update traceIds':
								if (event.traceIds) {
									api?.core?.actions.execute(updateTraceIds(event.traceIds));
								}
								break;
							case 'update idMap':
								if (event.context?.idMap) {
									api?.core?.actions.execute(updateIdMap(event.context.idMap));
								}
								break;
							default:
								break;
						}
					}).then((cleanup) => {
						if (!cleanup) {
							return;
						}
						// If cancel/reset happened before cleanup was available, abort immediately.
						if (cancelled) {
							cleanup();
							abortStreaming = undefined;
							return;
						}
						abortStreaming = cleanup;
					});
				},

				destroy() {
					clearAgentReviewTimer();
					abortStreaming?.();
					streamFlowPacer?.destroy();
				},
			};
		},
	});
};

/**
 * Gets the plugin state from an editor state.
 */
export const getAIExperienceContextPluginState = (
	state: EditorState,
): SessionContextPluginState | undefined => {
	return aiSessionContextPluginKey.getState(state);
};

export const createCommand = (action: SessionContextAction): EditorCommand => {
	return ({ tr }: { tr: Transaction }) => {
		return tr.setMeta(aiSessionContextPluginKey, action);
	};
};
